// src/pages/DashboardPage.js

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Container,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Stack,
  Grid,
  Collapse,
  IconButton,
} from '@mui/material'
import PieChartIcon from '@mui/icons-material/PieChart'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'

import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'

import {
  PieChart,
  Pie,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Label,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from 'recharts'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

import * as XLSX from 'xlsx';
import { GridToolbarContainer } from '@mui/x-data-grid';
import { Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

export default function DashboardPage() {
  // --------------------------------------------------------------------------
  // AUTH
  // --------------------------------------------------------------------------
  const { currentUser, userRole } = useAuth()
  const { t } = useTranslation() // i18n hook

  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  const [workspaces, setWorkspaces] = useState([])
  const [selectedWorkspace, setSelectedWorkspace] = useState(null)
  const [workspaceLoading, setWorkspaceLoading] = useState(true)

  const [allChannelsInWorkspace, setAllChannelsInWorkspace] = useState([])
  const [channelDonutData, setChannelDonutData] = useState([])
  const [channelDonutLoading, setChannelDonutLoading] = useState(false)

  const [formDonutData, setFormDonutData] = useState([])
  const [formDonutLoading, setFormDonutLoading] = useState(false)

  const [channels, setChannels] = useState([])
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [channelsLoading, setChannelsLoading] = useState(false)

  const [questionForms, setQuestionForms] = useState([])
  const [selectedForm, setSelectedForm] = useState(null)
  const [formsLoading, setFormsLoading] = useState(false)

  const [rawRows, setRawRows] = useState([])
  const [loadingAnswers, setLoadingAnswers] = useState(false)

  const [availableTags, setAvailableTags] = useState([])
  const [selectedTags, setSelectedTags] = useState([])

  const [usersMap, setUsersMap] = useState({})

  const [pivotMode, setPivotMode] = useState('none')
  const [pivotColumns, setPivotColumns] = useState([])
  const [pivotRows, setPivotRows] = useState([])

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false })

  const [dailyTrendData, setDailyTrendData] = useState([])
  const [dailyTrendLoading, setDailyTrendLoading] = useState(false)

  // 1. Yeni state tanımı: kullanıcı durum gruplarını tutmak için
  const [userStatusGroups, setUserStatusGroups] = useState({
    completed: [],
    inProgress: [],
    notStarted: []
  });

  // Durum kutularının açık/kapalı bilgisini tutan state
  const [expandedGroups, setExpandedGroups] = useState({
    notStarted: false,
    inProgress: false,
    completed: false
  })

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group]
    }))
  }

  // --------------------------------------------------------------------------
  // 0) Fetch all users -> usersMap
  // --------------------------------------------------------------------------
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const userSnap = await getDocs(collection(db, 'users'))
        const tempMap = {}
        userSnap.forEach((docSnap) => {
          const data = docSnap.data()
          tempMap[docSnap.id] = data.nickname || docSnap.id
        })
        setUsersMap(tempMap)
      } catch (error) {
        console.error('users alınırken hata:', error)
      }
    }
    fetchAllUsers()
  }, [])

  // --------------------------------------------------------------------------
  // 1) Workspace List (live onSnapshot)
  // --------------------------------------------------------------------------
  useEffect(() => {
    const wsRef = collection(db, 'workspaces')
    const unsubscribe = onSnapshot(
      wsRef,
      async (snapshot) => {
        try {
          let wsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          }))

          if (userRole === 'admin') {
            setWorkspaces(wsData)
            setWorkspaceLoading(false)
          } else {
            const allWsIds = wsData.map((ws) => ws.id)
            if (allWsIds.length === 0) {
              setWorkspaces([])
              setWorkspaceLoading(false)
              return
            }

            const channelsRef = collection(db, 'channels')
            const qCh = query(channelsRef, where('workspaceId', 'in', allWsIds))
            const chSnap = await getDocs(qCh)
            const relevantWsIds = new Set()
            chSnap.forEach((chDoc) => {
              const chData = chDoc.data()
              if (
                chData.memberDetails &&
                chData.memberDetails.some(
                  (md) => md.userId === currentUser.uid && md.role === 'moderator'
                )
              ) {
                relevantWsIds.add(chData.workspaceId)
              }
            })
            wsData = wsData.filter((ws) => relevantWsIds.has(ws.id))
            setWorkspaces(wsData)
            setWorkspaceLoading(false)
          }
        } catch (err) {
          console.error('Workspace okunurken hata:', err)
          setSnackbar({
            open: true,
            message: t('dashboard.workspaceLoadingError'),
            severity: 'error'
          })
          setWorkspaceLoading(false)
        }
      },
      (err) => {
        console.error('Workspace okunurken hata (snapshot):', err)
        setSnackbar({
          open: true,
          message: t('dashboard.workspaceSnapshotError'),
          severity: 'error'
        })
        setWorkspaceLoading(false)
      }
    )

    return () => unsubscribe()
  }, [userRole, currentUser, t])

  // --------------------------------------------------------------------------
  // 2) On workspace selection -> fetch channels + Donut Data
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedWorkspace) {
      setAllChannelsInWorkspace([])
      setChannelDonutData([])
      setFormDonutData([])
      return
    }

    const fetchChannelsInWorkspace = async () => {
      try {
        setChannelDonutLoading(true)
        setFormDonutLoading(true)

        // 2a) Get all channels of this workspace
        const chRef = collection(db, 'channels')
        const qCh = query(chRef, where('workspaceId', '==', selectedWorkspace.id))
        const chSnap = await getDocs(qCh)
        let allChannels = chSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))

        if (userRole !== 'admin') {
          allChannels = allChannels.filter((ch) => {
            if (!ch.memberDetails) return false
            return ch.memberDetails.some(
              (md) => md.userId === currentUser.uid && md.role === 'moderator'
            )
          })
        }

        setAllChannelsInWorkspace(allChannels)

        // 2b) Channel donut data
        const channelDonutPromises = allChannels.map(async (ch) => {
          const totalUsers = ch.memberDetails ? ch.memberDetails.length : 0
          const respRef = collection(db, 'responses')
          const qResp = query(respRef, where('channelId', '==', ch.id))
          const respSnap = await getDocs(qResp)
          const userIdSet = new Set()
          respSnap.docs.forEach((respDoc) => {
            const rData = respDoc.data()
            if (rData.userId) userIdSet.add(rData.userId)
          })
          return {
            channelId: ch.id,
            channelName: ch.name || '(isimsiz)',
            totalUsers,
            answeredUsers: userIdSet.size
          }
        })
        const channelDataTemp = await Promise.all(channelDonutPromises)
        setChannelDonutData(channelDataTemp)
        setChannelDonutLoading(false)
        
        const qFormRef = collection(db, 'questionForms');
        // Form donüt datası için, tüm formların alınması
        const formsPromises = allChannels.map(async (ch) => {
          const fQuery = query(qFormRef, where('channelId', '==', ch.id),where('status', '==', 'published'));
          const formsSnap = await getDocs(fQuery);
          return formsSnap.docs.map((doc) => ({
            id: doc.id,
            channelId: ch.id,
            channelName: ch.name || '(isimsiz)',
            ...doc.data(),
          }));
        });

        const allFormsArrays = await Promise.all(formsPromises);
        const allFormsInWorkspace = allFormsArrays.flat(); // Bu satırda tanımlanıyor

        // 2c) Form donut data (güncellenmiş versiyon)
        const formDataPromises = allFormsInWorkspace.map(async (form) => {
          const totalUsers = form?.memberDetails
            ? form.memberDetails.length
            : (
                allChannels.find((c) => c.id === form.channelId)?.memberDetails?.length || 0
              );
              
          const respRef = collection(db, 'responses');
          const qResp = query(
            respRef,
            where('channelId', '==', form.channelId),
            where('questionFormId', '==', form.id)
          );
          const respSnap = await getDocs(qResp);

          // Durum bazında kullanıcıları unique saymak için set kullanıyoruz
          const completedUsers = new Set();
          const inProgressUsers = new Set();

          respSnap.docs.forEach((respDoc) => {
            const rData = respDoc.data();
            if (rData.userId && rData.status) {
              if (rData.status === 'completed') {
                completedUsers.add(rData.userId);
              } else if (rData.status === 'inProgress') {
                inProgressUsers.add(rData.userId);
              }
            }
          });

          const completed = completedUsers.size;
          const inProgress = inProgressUsers.size;
          // Başlamayan: toplam üye - (completed + inProgress)
          const notStarted = Math.max(totalUsers - (completed + inProgress), 0);

          return {
            formId: form.id,
            formName: form.name || '(No name)',
            channelName: form.channelName,
            totalUsers,
            completed,
            inProgress,
            notStarted,
          };
        });

        const formDataTemp = await Promise.all(formDataPromises)
        setFormDonutData(formDataTemp)
        setFormDonutLoading(false)
      } catch (err) {
        console.error('Kanallar/Formlar alınırken hata:', err)
        setSnackbar({
          open: true,
          message: t('dashboard.channelFormFetchError'),
          severity: 'error'
        })
        setChannelDonutLoading(false)
        setFormDonutLoading(false)
      }
    }

    fetchChannelsInWorkspace()
  }, [selectedWorkspace, userRole, currentUser, t])

  // --------------------------------------------------------------------------
  // 2b) Daily Trend
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedWorkspace) {
      setDailyTrendData([])
      return
    }

    const fetchDailyTrends = async () => {
      try {
        setDailyTrendLoading(true)

        // Get channels in workspace
        const chRef = collection(db, 'channels')
        const qCh = query(chRef, where('workspaceId', '==', selectedWorkspace.id))
        const chSnap = await getDocs(qCh)
        let allChannels = chSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))

        if (userRole !== 'admin') {
          allChannels = allChannels.filter((ch) => {
            if (!ch.memberDetails) return false
            return ch.memberDetails.some(
              (md) => md.userId === currentUser.uid && md.role === 'moderator'
            )
          })
        }

        const responsePromises = allChannels.map(async (ch) => {
          const respRef = collection(db, 'responses')
          const qResp = query(respRef, where('channelId', '==', ch.id))
          const respSnap = await getDocs(qResp)
          return respSnap.docs.map((respDoc) => respDoc.data())
        })
        const allChannelsResponsesArray = await Promise.all(responsePromises)
        const allResponses = allChannelsResponsesArray.flat()

        const dailyMap = {}
        allResponses.forEach((resp) => {
          if (!resp.createdAt?.toDate) return
          const dateObj = resp.createdAt.toDate()
          const dateStr = dateObj.toISOString().slice(0, 10)

          if (!dailyMap[dateStr]) {
            dailyMap[dateStr] = {
              totalAnswers: 0,
              userIds: new Set()
            }
          }
          const answerCount = Array.isArray(resp.finalAnswers)
            ? resp.finalAnswers.length
            : 0

          dailyMap[dateStr].totalAnswers += answerCount
          if (resp.userId) dailyMap[dateStr].userIds.add(resp.userId)
        })

        const trendArray = Object.entries(dailyMap).map(([dateStr, val]) => ({
          date: dateStr,
          totalAnswers: val.totalAnswers,
          totalUsers: val.userIds.size
        }))
        trendArray.sort((a, b) => a.date.localeCompare(b.date))

        setDailyTrendData(trendArray)
        setDailyTrendLoading(false)
      } catch (error) {
        console.error('Günlük trend verisi alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('dashboard.dailyTrendFetchError'),
          severity: 'error'
        })
        setDailyTrendLoading(false)
      }
    }

    fetchDailyTrends()
  }, [selectedWorkspace, userRole, currentUser, t])

  // --------------------------------------------------------------------------
  // 3) Kanal Listesi
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedWorkspace) {
      setChannels([])
      setSelectedChannel(null)
      return
    }

    const fetchChannels = async () => {
      try {
        setChannelsLoading(true)
        const chRef = collection(db, 'channels')
        const qChannel = query(chRef, where('workspaceId', '==', selectedWorkspace.id))
        const chSnap = await getDocs(qChannel)
        let allChannels = chSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))

        if (userRole === 'admin') {
          setChannels(allChannels)
        } else {
          const modChannels = allChannels.filter((ch) => {
            if (!ch.memberDetails) return false
            return ch.memberDetails.some(
              (md) => md.userId === currentUser.uid && md.role === 'moderator'
            )
          })
          setChannels(modChannels)
        }

        setChannelsLoading(false)
      } catch (err) {
        console.error('Kanal listesi alınırken hata:', err)
        setSnackbar({
          open: true,
          message: t('dashboard.channelListError'),
          severity: 'error'
        })
        setChannelsLoading(false)
      }
    }

    setAvailableTags(selectedWorkspace.tags || [])
    setSelectedTags([])
    fetchChannels()
  }, [selectedWorkspace, currentUser, userRole, t])

  // --------------------------------------------------------------------------
  // 4) Fetch forms for the selected channel
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedChannel) {
      setQuestionForms([])
      setSelectedForm(null)
      return
    }

    const fetchForms = async () => {
      try {
        setFormsLoading(true)
        const ref = collection(db, 'questionForms')
        const qForms = query(
          ref,
          where('channelId', '==', selectedChannel.id),
          where('status', '==', 'published')
        );
        const formsSnap = await getDocs(qForms)
        const formsData = formsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        setQuestionForms(formsData)
        setFormsLoading(false)
      } catch (error) {
        console.error('Form listesi alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('dashboard.formListError'),
          severity: 'error'
        })
        setFormsLoading(false)
      }
    }

    fetchForms()
  }, [selectedChannel, t])

  // --------------------------------------------------------------------------
  // 5) Fetch selected form's answers
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedForm) {
      setRawRows([]);
      setUserStatusGroups({ completed: [], inProgress: [], notStarted: [] });
      return;
    }
  
    const fetchAnswers = async () => {
      try {
        setLoadingAnswers(true);
        const respRef = collection(db, 'responses');
        const qResp = query(
          respRef,
          where('channelId', '==', selectedChannel.id),
          where('questionFormId', '==', selectedForm.id)
        );
        const respSnap = await getDocs(qResp);
        const rawResponses = respSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
  
        // Kullanıcı durumlarını hesaplayalım (her response dokümanında "status" alanı bulunduğu varsayılıyor)
        const statusMap = {};
        rawResponses.forEach((resp) => {
          if (resp.userId && resp.status) {
            statusMap[resp.userId] = resp.status;
          }
        });
  
        // Seçili kanalın memberDetails'ından tüm kullanıcıları alalım ve durumlarını belirleyelim
        const completedUsers = [];
        const inProgressUsers = [];
        const notStartedUsers = [];
        if (selectedChannel.memberDetails && Array.isArray(selectedChannel.memberDetails)) {
          selectedChannel.memberDetails.forEach((md) => {
            const userId = md.userId;
            // Burada usersMap'ten kullanıcı adı (nickname) alınır, yoksa userId kullanılır.
            const nickname = usersMap[userId] || userId;
            const status = statusMap[userId];
            if (status === 'completed') {
              completedUsers.push(nickname);
            } else if (status === 'inProgress') {
              inProgressUsers.push(nickname);
            } else {
              notStartedUsers.push(nickname);
            }
          });
        }
        setUserStatusGroups({
          completed: completedUsers,
          inProgress: inProgressUsers,
          notStarted: notStartedUsers
        });
  
        // Ayrıntılı tablo için tüm satırları oluşturma (mevcut kodun devamı)
        const userTagMap = {};
        selectedChannel.memberDetails?.forEach((md) => {
          userTagMap[md.userId] = md.tags || [];
        });
  
        const allRows = [];
        rawResponses.forEach((resp) => {
          const { userId, finalAnswers, createdAt } = resp;
          const userEmail = usersMap[userId] || userId;
          const userTags = userTagMap[userId] || [];
          const dateStr = createdAt?.toDate ? createdAt.toDate().toLocaleString() : '';
  
          if (!Array.isArray(finalAnswers)) return;
  
          finalAnswers.forEach((fa, idx) => {
            let plainAnswer = '';
          
            if (typeof fa.answer === 'object' && fa.answer !== null) {
              // Eğer text varsa, onu kullan; ayrıca media verisi de varsa ek bir not ekle.
              if (typeof fa.answer.text === 'string' && fa.answer.text.trim().length > 0) {
                plainAnswer = fa.answer.text;
                if (
                  (fa.answer.media && fa.answer.media.length > 0) ||
                  fa.answer.videoUrl ||
                  fa.answer.collageSingleUrl
                ) {
                  // Media datasının da mevcut olduğunu belirt.
                  plainAnswer += ' (' + t('dashboard.mediaDataExists') + ')';
                }
              } else if (fa.answer?.media && fa.answer.media.length > 0) {
                // Sadece media verisi varsa, media cevabı döndür.
                plainAnswer = t('dashboard.mediaAnswer');
              } else if (fa.answer?.videoUrl) {
                plainAnswer = t('dashboard.mediaAnswer');
              } else if (fa.answer?.collageSingleUrl) {
                plainAnswer = t('dashboard.mediaAnswer');
              } else if (Array.isArray(fa.answer)) {
                // AI (Conversation) verisi için kontrol
                const arr = fa.answer;
                const isAiModeratorArray = arr.length > 0 && arr.every(
                  (msgObj) =>
                    msgObj &&
                    typeof msgObj === 'object' &&
                    'role' in msgObj &&
                    'content' in msgObj
                );
                if (isAiModeratorArray) {
                  plainAnswer = arr
                    .map((msgObj) => `[${msgObj.role}] ${msgObj.content}`)
                    .join('<br/>');
                } else {
                  plainAnswer = arr.join(', ');
                }
              } else {
                plainAnswer = '';
              }
            } else if (Array.isArray(fa.answer)) {
              plainAnswer = fa.answer.join(', ');
            } else if (typeof fa.answer === 'string') {
              plainAnswer = fa.answer;
            }
          
            allRows.push({
              id: resp.id + '_' + idx,
              userEmail,
              userTags,
              questionText: fa.questionText,
              answer: plainAnswer,
              createdAt: dateStr
            });
          });
        });
  
        setRawRows(allRows);
        setLoadingAnswers(false);
      } catch (error) {
        console.error('Cevaplar alınırken hata:', error);
        setSnackbar({
          open: true,
          message: t('dashboard.answersLoadingError'),
          severity: 'error'
        });
        setLoadingAnswers(false);
      }
    };
  
    fetchAnswers();
  }, [selectedForm, selectedChannel, usersMap, t]);

  // --------------------------------------------------------------------------
  // PIVOT BUILDER
  // --------------------------------------------------------------------------
  const buildPivotData = useCallback(
    (mode, originalRows) => {
      if (mode === 'none') {
        return { columns: [], rows: [] }
      }
      if (originalRows.length === 0) {
        return { columns: [], rows: [] }
      }

      const uniqueUsers = [...new Set(originalRows.map((r) => r.userEmail))]
      const uniqueQuestions = [...new Set(originalRows.map((r) => r.questionText))]

      // HTML içeriğini temizleyen yardımcı fonksiyon
      const stripHtml = (html) => {
        const div = document.createElement('div');
        div.innerHTML = html || '';
        return div.textContent || div.innerText || '';
      };

      if (mode === 'userPivot') {
        const pivotCols = [
          { field: 'userEmail', headerName: t('dashboard.columnUserEmail'), width: 200 }
        ];
        uniqueQuestions.forEach((q, idx) => {
          pivotCols.push({
            field: `question_${idx}`,
            headerName: stripHtml(q).substring(0, 50),
            flex: 1
          });
        });
      
        const pivotRows = uniqueUsers.map((user, rowIndex) => {
          const rowData = {
            id: 'row_' + rowIndex,
            userEmail: user
          };
          uniqueQuestions.forEach((q, colIndex) => {
            const match = originalRows.find(
              (rr) => rr.userEmail === user && rr.questionText === q
            );
            rowData[`question_${colIndex}`] = match ? stripHtml(match.answer) : '';
          });
          return rowData;
        });
      
        return { columns: pivotCols, rows: pivotRows };
      }

      if (mode === 'questionPivot') {
        const pivotCols = [
          { field: 'questionText', headerName: t('dashboard.columnQuestion'), width: 300 }
        ];
        uniqueUsers.forEach((u, idx) => {
          pivotCols.push({
            field: `user_${idx}`,
            headerName: u.substring(0, 40),
            flex: 1
          });
        });
      
        const pivotRows = uniqueQuestions.map((q, rowIndex) => {
          const rowData = {
            id: 'row_' + rowIndex,
            // HTML etiketlerinden arındırılmış soru metni
            questionText: stripHtml(q)
          };
          uniqueUsers.forEach((u, colIndex) => {
            const match = originalRows.find(
              (rr) => rr.userEmail === u && rr.questionText === q
            );
            // Cevap hücresinde de HTML temizlemesi yapılıyor
            rowData[`user_${colIndex}`] = match ? stripHtml(match.answer) : '';
          });
          return rowData;
        });
      
        return { columns: pivotCols, rows: pivotRows };
      }

      return { columns: [], rows: [] }
    },
    [t]
  )

  // --------------------------------------------------------------------------
  // Filtered rows by selectedTags
  // --------------------------------------------------------------------------
  const filteredRawRows = useMemo(() => {
    if (selectedTags.length === 0) return rawRows
    return rawRows.filter((r) =>
      selectedTags.some((tag) => r.userTags.includes(tag))
    )
  }, [rawRows, selectedTags])

  // --------------------------------------------------------------------------
  // Pivot Data Rebuild
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (pivotMode === 'none') {
      setPivotColumns([])
      setPivotRows([])
      return
    }
    const { columns, rows } = buildPivotData(pivotMode, filteredRawRows)
    setPivotColumns(columns)
    setPivotRows(rows)
  }, [pivotMode, filteredRawRows, buildPivotData])

  // --------------------------------------------------------------------------
  // Normal table columns
  // --------------------------------------------------------------------------

  
  const normalColumns = useMemo(
    () => [
      {
        field: 'userEmail',
        headerName: t('dashboard.columnUserEmail'),
        flex: 1
      },
      {
        field: 'userTags',
        headerName: t('dashboard.columnUserTags'),
        flex: 1,
        renderCell: (params) => (params.value || []).join(', ')
      },
      {
        field: 'questionText',
        headerName: t('dashboard.columnQuestion'),
        flex: 2,
        renderCell: (params) => {
          const div = document.createElement('div')
          div.innerHTML = params.value || ''
          const plainText = div.textContent || div.innerText || ''
          return <span>{plainText}</span>
        }
      },
      {
        field: 'answer',
        headerName: t('dashboard.columnAnswer'),
        flex: 2,
        renderCell: (params) => {
          const div = document.createElement('div')
          div.innerHTML = params.value || ''
          const plainText = div.textContent || div.innerText || ''
          return <span>{plainText}</span>
        }
      },
      {
        field: 'createdAt',
        headerName: t('dashboard.columnCreatedAt'),
        flex: 1
      }
    ],
    [t]
  )

  // --------------------------------------------------------------------------
  // COLORS for Donut Charts
  // --------------------------------------------------------------------------
  const COLORS = ['#0088FE', '#FF8042']
  // Donut grafik renkleri (Form için)
  const FORM_COLORS = ['#43a047', '#fb8c00', '#e53935']; // tamamlanan, devam eden, başlamayan renkleri



  const handleExportToExcel = () => {
    let rows, columns;
    if (pivotMode === 'none') {
      rows = filteredRawRows;
      columns = normalColumns;
    } else {
      rows = pivotRows;
      columns = pivotColumns;
    }
  
    // Veri yoksa uyarı göster
    if (rows.length === 0) {
      setSnackbar({
        open: true,
        message: t('dashboard.noDataToExport'),
        severity: 'warning'
      });
      return;
    }
  
    // Satırları headerName'lere göre dönüştür ve HTML'i temizle
    const transformedRows = rows.map(row => {
      const newRow = {};
      columns.forEach(col => {
        let value = row[col.field];
        // Normal modda questionText ve answer için HTML temizleme
        if (pivotMode === 'none' && (col.field === 'questionText' || col.field === 'answer')) {
          const div = document.createElement('div');
          div.innerHTML = value || '';
          value = div.textContent || div.innerText || '';
        }
        newRow[col.headerName] = value;
      });
      return newRow;
    });
  
    // Excel çalışma sayfasını oluştur
    const worksheet = XLSX.utils.json_to_sheet(transformedRows, {
      header: columns.map(col => col.headerName)
    });
  
    // Çalışma kitabını oluştur ve sayfayı ekle
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  
    // Dosyayı indir
    XLSX.writeFile(workbook, 'DashboardData.xlsx');
  };
  
  // Özel araç çubuğu bileşeni
  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbar />
        <Button
          color="primary"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportToExcel}
        >
          {t('dashboard.exportToExcel')}
        </Button>
      </GridToolbarContainer>
    );
  };


  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <Container
      sx={{
        mt: 4,
        my: 2,
        py: 3,
        display: 'flex',
        padding: 4,
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#f9f9f9',
        borderRadius: 2
      }}
    >
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#3f51b5' }}>
        <PieChartIcon sx={{ mr: 1 }} />
        {t('dashboard.pageTitle')}
      </Typography>

      {/* 1) WORKSPACE Selection */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          width: '100%',
          maxWidth: 900,
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
        }}
      >
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('dashboard.selectWorkspaceTitle')}
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel id="workspace-select-label">Workspace</InputLabel>
          <Select
            labelId="workspace-select-label"
            label="Workspace"
            value={selectedWorkspace?.id || ''}
            onChange={(e) => {
              const wsId = e.target.value
              const wsObj = workspaces.find((w) => w.id === wsId)
              setSelectedWorkspace(wsObj || null)
              setSelectedChannel(null)
              setSelectedForm(null)
            }}
          >
            <MenuItem value="">{t('dashboard.noWorkspaceSelected')}</MenuItem>
            {workspaces.map((ws) => (
              <MenuItem key={ws.id} value={ws.id}>
                {ws.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* 2) DASHBOARD CHARTS */}
      {selectedWorkspace && (
        <>
          {/* Daily Trend Chart */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              width: '100%',
              maxWidth: 900,
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('dashboard.dailyTrendTitle')}
            </Typography>
            {dailyTrendLoading ? (
              <Box textAlign="center">
                <CircularProgress />
              </Box>
            ) : dailyTrendData.length === 0 ? (
              <Typography>{t('dashboard.noAnswersInWorkspace')}</Typography>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 300,
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  p: 1
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="totalAnswers"
                      stroke="#82ca9d"
                      name="Toplam Cevap"
                      isAnimationActive
                      animationDuration={800}
                    />
                    <Line
                      type="monotone"
                      dataKey="totalUsers"
                      stroke="#8884d8"
                      name="Kişi Sayısı"
                      isAnimationActive
                      animationDuration={800}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          {/* CHANNEL DONUT CHARTS */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              width: '100%',
              maxWidth: 900,
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
              mx: 'auto'
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('dashboard.channelsTitle')}
            </Typography>

            {channelDonutLoading ? (
              <Box textAlign="center">
                <CircularProgress />
              </Box>
            ) : channelDonutData.length === 0 ? (
              <Typography>{t('dashboard.noChannelsInWorkspace')}</Typography>
            ) : (
              <Grid container spacing={2} justifyContent="center">
                {channelDonutData.map((chItem) => {
                  const notAnswered = chItem.totalUsers - chItem.answeredUsers
                  const donutData = [
                    { name: 'Aktif', value: chItem.answeredUsers },
                    { name: 'Aktif Değil', value: Math.max(notAnswered, 0) }
                  ]
                  const answeredRatio = chItem.totalUsers
                    ? ((chItem.answeredUsers / chItem.totalUsers) * 100).toFixed(1)
                    : 0

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      key={chItem.channelId}
                      sx={{ mx: 'auto' }}
                    >
                      <Box textAlign="center">
                        <Box
                          sx={{
                            width: 180,
                            height: 180,
                            mx: 'auto',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                        >
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={donutData}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={70}
                                dataKey="value"
                                isAnimationActive
                                animationDuration={800}
                              >
                                {donutData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                                <Label
                                  value={`${answeredRatio}%`}
                                  position="center"
                                  fill="#333"
                                  style={{ fontSize: '14px', fontWeight: 'bold' }}
                                />
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                          {chItem.channelName}
                        </Typography>
                        <Typography variant="caption">
                          {chItem.answeredUsers} / {chItem.totalUsers}
                        </Typography>
                      </Box>
                    </Grid>
                  )
                })}
              </Grid>
            )}
          </Paper>

          {/* FORM DONUT CHARTS */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              width: '100%',
              maxWidth: 900,
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
              mx: 'auto'
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('dashboard.formsTitle')}
            </Typography>

            {formDonutLoading ? (
              <Box textAlign="center">
                <CircularProgress />
              </Box>
            ) : formDonutData.length === 0 ? (
              <Typography>{t('dashboard.noFormsInWorkspace')}</Typography>
            ) : (
              <Grid container spacing={2} justifyContent="center">
                {formDonutData.map((fItem) => {
                // 3 kategori: Completed, In Progress ve Not Started
                const donutData = [
                  { name: t('dashboard.progressReportCompleted'), value: fItem.completed },
                  { name: t('dashboard.progressReportInprogress'), value: fItem.inProgress },
                  { name: t('dashboard.progressReportNotStart'), value: fItem.notStarted }
                ];
                const completedRatio = fItem.totalUsers
                  ? ((fItem.completed / fItem.totalUsers) * 100).toFixed(1)
                  : 0;

                return (
                  <Grid item xs={12} sm={6} md={3} key={fItem.formId} sx={{ mx: 'auto' }}>
                    <Box textAlign="center">
                      <Box
                        sx={{
                          width: 180,
                          height: 180,
                          mx: 'auto',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      >
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={donutData}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={70}
                              dataKey="value"
                              isAnimationActive
                              animationDuration={800}
                            >
                              {donutData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={FORM_COLORS[index % FORM_COLORS.length]}
                                />
                              ))}
                              <Label
                                value={`${completedRatio}%`}
                                position="center"
                                fill="#333"
                                style={{ fontSize: '14px', fontWeight: 'bold' }}
                              />
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                        {fItem.formName}
                      </Typography>
                      <Typography variant="caption">
                        {t('dashboard.channelSelectLabel')}: {fItem.channelName}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {fItem.completed} / {fItem.totalUsers}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
              </Grid>
            )}
          </Paper>
        </>
      )}

      {/* 3) Channel / Form / Filter Parameters */}
      {selectedWorkspace && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            width: '100%',
            maxWidth: 900,
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('dashboard.channelFormFiltersTitle')}
          </Typography>
          <Stack spacing={2}>
            {/* Kanal Seçimi */}
            <FormControl fullWidth size="small">
              <InputLabel id="channel-select-label">{t('dashboard.channelSelectLabel')}</InputLabel>
              <Select
                labelId="channel-select-label"
                label={t('dashboard.channelSelectLabel')}
                value={selectedChannel?.id || ''}
                onChange={(e) => {
                  const chId = e.target.value
                  const chObj = channels.find((c) => c.id === chId)
                  setSelectedChannel(chObj || null)
                  setSelectedForm(null)
                }}
              >
                <MenuItem value="">{t('dashboard.noWorkspaceSelected')}</MenuItem>
                {channelsLoading ? (
                  <MenuItem disabled>{t('dashboard.channelLoading')}</MenuItem>
                ) : (
                  channels.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Form Seçimi */}
            {selectedChannel && (
              <FormControl fullWidth size="small">
                <InputLabel id="form-select-label">{t('dashboard.formSelectLabel')}</InputLabel>
                <Select
                  labelId="form-select-label"
                  label={t('dashboard.formSelectLabel')}
                  value={selectedForm?.id || ''}
                  onChange={(e) => {
                    const fId = e.target.value
                    const fObj = questionForms.find((f) => f.id === fId)
                    setSelectedForm(fObj || null)
                  }}
                >
                  <MenuItem value="">{t('dashboard.noWorkspaceSelected')}</MenuItem>
                  {formsLoading ? (
                    <MenuItem disabled>{t('dashboard.formLoading')}</MenuItem>
                  ) : (
                    questionForms.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}

            {/* Tag Filtre */}
            {selectedChannel && availableTags.length > 0 && (
              <Autocomplete
                multiple
                options={availableTags}
                value={selectedTags}
                onChange={(event, newValue) => setSelectedTags(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('dashboard.tagLabel')}
                    placeholder={t('dashboard.tagPlaceholder')}
                    size="small"
                  />
                )}
              />
            )}

            {/* Pivot Mode Seçimi */}
            {selectedForm && (
              <FormControl size="small" sx={{ maxWidth: 250 }}>
                <InputLabel id="pivot-mode-label">{t('dashboard.pivotModeLabel')}</InputLabel>
                <Select
                  labelId="pivot-mode-label"
                  label={t('dashboard.pivotModeLabel')}
                  value={pivotMode}
                  onChange={(e) => setPivotMode(e.target.value)}
                >
                  <MenuItem value="none">{t('dashboard.pivotNone')}</MenuItem>
                  <MenuItem value="userPivot">{t('dashboard.pivotUser')}</MenuItem>
                  <MenuItem value="questionPivot">{t('dashboard.pivotQuestion')}</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
        </Paper>
      )}

      {/* 4) Detailed Table or Pivot */}
      {selectedForm && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 3,
            width: '100%',
            maxWidth: 900,
            backgroundColor: '#fefefe',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 'bold',
              textAlign: 'center',
              color: '#3f51b5'
            }}
          >
            {t('dashboard.progressReport')}
          </Typography>
          <Grid container spacing={3}>
            {/* Başlamayanlar */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  backgroundColor: '#ffebee',
                  borderLeft: '6px solid #e53935',
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => toggleGroup('notStarted')}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: '#c62828', mb: 1 }}
                  >
                    {t('dashboard.progressReportNotStart')} ({userStatusGroups.notStarted.length})
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleGroup('notStarted')
                    }}
                  >
                    {expandedGroups.notStarted ? (
                      <ExpandLessIcon sx={{ color: '#c62828' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#c62828' }} />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={expandedGroups.notStarted} timeout="auto" unmountOnExit>
                  {userStatusGroups.notStarted.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#555' }}>
                      {t('dashboard.progressReportNotUser')}
                    </Typography>
                  ) : (
                    userStatusGroups.notStarted.map((nickname) => (
                      <Typography key={nickname} variant="body2" sx={{ color: '#c62828' }}>
                        {nickname}
                      </Typography>
                    ))
                  )}
                </Collapse>
              </Box>
            </Grid>
            {/* Devam Edenler */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  backgroundColor: '#fff3e0',
                  borderLeft: '6px solid #fb8c00',
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => toggleGroup('inProgress')}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: '#e65100', mb: 1 }}
                  >
                    {t('dashboard.progressReportInprogress')} ({userStatusGroups.inProgress.length})
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleGroup('inProgress')
                    }}
                  >
                    {expandedGroups.inProgress ? (
                      <ExpandLessIcon sx={{ color: '#e65100' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#e65100' }} />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={expandedGroups.inProgress} timeout="auto" unmountOnExit>
                  {userStatusGroups.inProgress.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#555' }}>
                      {t('dashboard.progressReportNotUser')}
                    </Typography>
                  ) : (
                    userStatusGroups.inProgress.map((nickname) => (
                      <Typography key={nickname} variant="body2" sx={{ color: '#e65100' }}>
                        {nickname}
                      </Typography>
                    ))
                  )}
                </Collapse>
              </Box>
            </Grid>
            {/* Tamamlananlar */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  backgroundColor: '#e8f5e9',
                  borderLeft: '6px solid #43a047',
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => toggleGroup('completed')}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 1 }}
                  >
                    {t('dashboard.progressReportCompleted')} ({userStatusGroups.completed.length})
                  </Typography>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleGroup('completed')
                    }}
                  >
                    {expandedGroups.completed ? (
                      <ExpandLessIcon sx={{ color: '#2e7d32' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: '#2e7d32' }} />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={expandedGroups.completed} timeout="auto" unmountOnExit>
                  {userStatusGroups.completed.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#555' }}>
                      {t('dashboard.progressReportNotUser')}
                    </Typography>
                  ) : (
                    userStatusGroups.completed.map((nickname) => (
                      <Typography key={nickname} variant="body2" sx={{ color: '#2e7d32' }}>
                        {nickname}
                      </Typography>
                    ))
                  )}
                </Collapse>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
      {selectedForm && (
        <>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
            {t('dashboard.detailedReport')}
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              height: 600,
              p: 1,
              mb: 2,
              width: '100%',
              maxWidth: 900,
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
            }}
          >
            {loadingAnswers ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {pivotMode === 'none' && (
                  <DataGrid
                    rows={filteredRawRows}
                    columns={normalColumns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectionOnClick
                    slots={{ toolbar: CustomToolbar }}
                    componentsProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                        csvOptions: {
                          fileName: 'ModeratorAnswers',
                          utf8WithBom: true
                        }
                      }
                    }}
                  />
                )}
                {pivotMode !== 'none' && (
                  <DataGrid
                    rows={pivotRows}
                    columns={pivotColumns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectionOnClick
                    slots={{ toolbar: CustomToolbar }}
                    componentsProps={{
                      toolbar: {
                        csvOptions: {
                          fileName: 'ModeratorAnswers_Pivot',
                          utf8WithBom: true
                        }
                      }
                    }}
                  />
                )}
              </>
            )}
          </Paper>
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}